import { text } from '../app/i18n';
import { formatDecimal } from './format';

/**
 * Widgets d'un tableau de bord alimentés par une requête nommée (`source`, S7f-d) : carte, graphique et
 * tableau déclarés en JSON (champs, formats, séries, lignes de référence, colonnes, ligne de total). Les
 * valeurs sont lues par des chemins pointés, jamais évaluées ; un montant s'affiche depuis sa chaîne
 * décimale exacte (R2, LO4). La géométrie d'un graphique est la seule conversion en nombre.
 */

type Localized = Record<string, string> | undefined;
type Format = 'money' | 'number' | 'percent' | 'date' | 'week' | 'text';
type Tone = 'neutral' | 'positive' | 'negative' | 'warning' | 'info' | 'accent';
interface Flag {
  field: string;
  label: Localized;
  tone?: Tone;
}

export interface SourceWidget {
  widget_id: string;
  kind: string;
  primitive?: string;
  title?: Localized;
  description?: Localized;
  source?: { query_name: string; parameters?: Record<string, string> };
  value_field?: string;
  value_format?: Format;
  value_tone_field?: string;
  caption_fields?: Array<{ field: string; format?: Format; label?: Localized }>;
  flag?: Flag;
  rows_field?: string;
  x_field?: string;
  x_format?: Format;
  series?: Array<{ field: string; label: Localized; mark: 'bar' | 'line'; tone?: Tone; dashed?: boolean; flag_field?: string }>;
  reference_lines?: Array<{ field: string; label: Localized; tone?: Tone }>;
  band?: { field: string; label: Localized };
  columns?: Array<{ field: string; label: Localized; format?: Format; tone?: Tone; flag?: Flag; optional?: boolean }>;
  total_row?: { label: Localized; field: string };
}

type Data = Record<string, unknown>;

/** Valeur d'un chemin pointé (`lowest.closing`) ; jamais une expression. */
export function pick(data: unknown, path: string | undefined): unknown {
  if (path === undefined) return undefined;
  let current: unknown = data;
  for (const part of path.split('.')) {
    if (current === null || typeof current !== 'object') return undefined;
    current = (current as Data)[part];
  }
  return current;
}

const DECIMAL = /^-?\d+(\.\d+)?$/;

/** Semaine ISO d'une date civile AAAA-MM-JJ, sans fuseau (R1). */
function isoWeek(date: string): number {
  const day = new Date(`${date}T00:00:00Z`);
  const thursday = new Date(day.getTime() + (3 - ((day.getUTCDay() + 6) % 7)) * 86_400_000);
  const firstThursday = new Date(Date.UTC(thursday.getUTCFullYear(), 0, 4));
  return 1 + Math.round(((thursday.getTime() - firstThursday.getTime()) / 86_400_000 - 3 + ((firstThursday.getUTCDay() + 6) % 7)) / 7);
}

export function formatField(value: unknown, format: Format | undefined, currency: string | undefined): string {
  if (value === undefined || value === null || value === '') return '—';
  if (format === 'money' && typeof value === 'string' && DECIMAL.test(value) && currency !== undefined) return formatDecimal(value, 'fr', { style: 'currency', currency });
  if ((format === 'week' || format === 'date') && typeof value === 'string' && /^\d{4}-\d{2}-\d{2}$/.test(value)) {
    const short = `${value.slice(8, 10)}/${value.slice(5, 7)}`;
    return format === 'week' ? `S${String(isoWeek(value)).padStart(2, '0')} · ${short}` : `${short}/${value.slice(0, 4)}`;
  }
  if (format === 'percent' && (typeof value === 'number' || (typeof value === 'string' && DECIMAL.test(value)))) return `${formatDecimal(value, 'fr', { maximumFractionDigits: 1 })} %`;
  if ((format === 'number' || format === undefined) && (typeof value === 'number' || (typeof value === 'string' && DECIMAL.test(value)))) return formatDecimal(value, 'fr', { maximumFractionDigits: 2 });
  return String(value);
}

const negative = (value: unknown): boolean => typeof value === 'string' ? value.startsWith('-') : typeof value === 'number' && value < 0;

/** Position d'un point du graphique : géométrie seulement, jamais une valeur affichée ni recalculée (R2). */
function coordinate(value: unknown): number | null {
  if (typeof value === 'number') return value;
  if (typeof value !== 'string' || !DECIMAL.test(value)) return null;
  return Number.parseFloat(value);
}

function FlagChip({ flag }: { flag: Flag }) {
  return <span className={`ay-flag ay-flag--${flag.tone ?? 'negative'}`}>{text(flag.label, 'fr', '')}</span>;
}

export function SourceKpi({ widget, data }: { widget: SourceWidget; data: Data }) {
  const currency = typeof data['currency'] === 'string' ? data['currency'] : undefined;
  const value = pick(data, widget.value_field);
  const tone = widget.value_tone_field !== undefined && negative(pick(data, widget.value_tone_field)) ? ' ay-kpi__value--negative' : '';
  const captions = (widget.caption_fields ?? []).map((caption) => {
    const shown = formatField(pick(data, caption.field), caption.format, currency);
    return caption.label === undefined ? shown : `${text(caption.label, 'fr', '')} ${shown}`;
  });
  return (
    <>
      <p className={`ay-kpi__value${tone}`}>{formatField(value, widget.value_format, currency)}</p>
      {(widget.flag !== undefined && pick(data, widget.flag.field) === true) || captions.length > 0 ? (
        <p className="ay-kpi__caption">
          {widget.flag !== undefined && pick(data, widget.flag.field) === true && <FlagChip flag={widget.flag} />}
          {captions.length > 0 && <span>{captions.join(' · ')}</span>}
        </p>
      ) : null}
    </>
  );
}

export function SourceTable({ widget, data }: { widget: SourceWidget; data: Data }) {
  const currency = typeof data['currency'] === 'string' ? data['currency'] : undefined;
  const rows = (pick(data, widget.rows_field) as Data[] | undefined) ?? [];
  // Une colonne facultative (clôture d'un scénario choisi) ne s'affiche que si une ligne la porte.
  const columns = (widget.columns ?? []).filter((column) => column.optional !== true || rows.some((row) => pick(row, column.field) !== undefined));
  const total = widget.total_row === undefined ? undefined : (pick(data, widget.total_row.field) as Data | undefined);
  if (rows.length === 0) return <p className="ay-kpi__state">Aucune donnée</p>;
  const numeric = (format: Format | undefined) => format === 'money' || format === 'number' || format === 'percent';
  return (
    <div className="ay-source-table">
      <table>
        <thead>
          <tr>
            {columns.map((column) => (
              <th key={column.field} scope="col" className={numeric(column.format) ? 'ay-source-table__num' : undefined}>
                {text(column.label, 'fr', column.field)}
              </th>
            ))}
          </tr>
        </thead>
        <tbody>
          {rows.map((row, index) => (
            <tr key={index}>
              {columns.map((column) => (
                <td key={column.field} className={`${numeric(column.format) ? 'ay-source-table__num' : ''} ay-tone--${column.tone ?? 'neutral'}`}>
                  {column.flag !== undefined && pick(row, column.flag.field) === true && <FlagChip flag={column.flag} />}
                  {formatField(pick(row, column.field), column.format, currency)}
                </td>
              ))}
            </tr>
          ))}
        </tbody>
        {total !== undefined && (
          <tfoot>
            <tr>
              {columns.map((column, index) => (
                <td key={column.field} className={numeric(column.format) ? 'ay-source-table__num' : undefined}>
                  {index === 0 ? text(widget.total_row!.label, 'fr', 'Total') : pick(total, column.field) === undefined ? '' : formatField(pick(total, column.field), column.format, currency)}
                </td>
              ))}
            </tr>
          </tfoot>
        )}
      </table>
    </div>
  );
}

const WIDTH = 1240;
const LEFT = 76;
const RIGHT = 10;

export function SourceChart({ widget, data }: { widget: SourceWidget; data: Data }) {
  const currency = typeof data['currency'] === 'string' ? data['currency'] : undefined;
  const rows = (pick(data, widget.rows_field) as Data[] | undefined) ?? [];
  if (rows.length === 0) return <p className="ay-kpi__state">Aucune donnée</p>;
  const series = widget.series ?? [];
  const label = (row: Data) => formatField(pick(row, widget.x_field), widget.x_format, currency);
  const step = (WIDTH - LEFT - RIGHT) / rows.length;
  const cx = (index: number) => LEFT + step * index + step / 2;
  const axisLabel = (value: number) => (currency === undefined ? formatDecimal(value, 'fr', { maximumFractionDigits: 0 }) : formatDecimal(value, 'fr', { style: 'currency', currency, maximumFractionDigits: 0 }));
  const title = text(widget.title, 'fr', widget.widget_id);

  if (widget.primitive === 'diverging_bar_chart') {
    const first = series[0];
    if (first === undefined) return null;
    const band = coordinate(pick(data, widget.band?.field)) ?? 0;
    const values = rows.map((row) => coordinate(pick(row, first.field)) ?? 0);
    const limit = Math.max(band * 1.6, ...values.map(Math.abs), 1) * 1.15;
    const height = 220;
    const y = (value: number) => 20 + (height - 50) * (0.5 - value / (2 * limit));
    const bar = Math.min(56, step * 0.5);
    return (
      <svg viewBox={`0 0 ${WIDTH} ${height}`} width="100%" role="img" aria-label={title} className="ay-chart">
        {band > 0 && <rect x={LEFT} y={y(band)} width={WIDTH - LEFT - RIGHT} height={y(-band) - y(band)} className="ay-chart__band" />}
        {band > 0 && widget.band !== undefined && (
          <text x={WIDTH - RIGHT - 4} y={y(band) + 13} textAnchor="end" className="ay-chart__note">
            {`${text(widget.band.label, 'fr', '')} ± ${formatField(pick(data, widget.band.field), 'money', currency)}`}
          </text>
        )}
        <line x1={LEFT} x2={WIDTH - RIGHT} y1={y(0)} y2={y(0)} className="ay-chart__axis" />
        {rows.map((row, index) => {
          const value = values[index]!;
          const up = value >= 0;
          const outside = Math.abs(value) > band;
          return (
            <g key={index} className={up ? 'ay-tone-fill--info' : 'ay-tone-fill--warning'}>
              <rect x={cx(index) - bar / 2} y={Math.min(y(value), y(0))} width={bar} height={Math.abs(y(value) - y(0))} rx={2} opacity={outside ? 1 : 0.5} />
              <text x={cx(index)} y={up ? y(value) - 6 : y(value) + 15} textAnchor="middle" className="ay-chart__value">
                {`${up ? '▲' : '▼'} ${formatField(pick(row, first.field), 'money', currency)}`}
              </text>
              <text x={cx(index)} y={height - 8} textAnchor="middle" className="ay-chart__label">
                {label(row)}
              </text>
            </g>
          );
        })}
      </svg>
    );
  }

  const height = 320;
  const references = (widget.reference_lines ?? []).map((reference) => ({ ...reference, value: coordinate(pick(data, reference.field)) })).filter((reference) => reference.value !== null);
  const all = [...rows.flatMap((row) => series.map((entry) => coordinate(pick(row, entry.field)) ?? 0)), ...references.map((reference) => reference.value!)];
  const max = Math.max(...all, 1) * 1.1;
  const min = Math.min(...all, 0) * 1.1;
  const y = (value: number) => 20 + (height - 60) * (1 - (value - min) / (max - min));
  const bars = series.filter((entry) => entry.mark === 'bar');
  const lines = series.filter((entry) => entry.mark === 'line');
  const width = Math.min(24, (step * 0.7) / Math.max(1, bars.length));
  const ticks = [0, 1, 2, 3, 4].map((index) => min + ((max - min) * index) / 4);
  return (
    <>
      <ul className="ay-chart__legend">
        {series.map((entry) => (
          <li key={entry.field}>
            <span className={`ay-chart__swatch ay-chart__swatch--${entry.mark}${entry.dashed === true ? ' ay-chart__swatch--dashed' : ''} ay-tone-fill--${entry.tone ?? 'neutral'}`} aria-hidden="true" />
            {text(entry.label, 'fr', entry.field)}
          </li>
        ))}
        {references.map((reference) => (
          <li key={reference.field}>
            <span className={`ay-chart__swatch ay-chart__swatch--line ay-chart__swatch--dashed ay-tone-fill--${reference.tone ?? 'negative'}`} aria-hidden="true" />
            {`${text(reference.label, 'fr', '')} ${formatField(pick(data, reference.field), 'money', currency)}`}
          </li>
        ))}
      </ul>
      <svg viewBox={`0 0 ${WIDTH} ${height}`} width="100%" role="img" aria-label={title} className="ay-chart">
        {ticks.map((tick) => (
          <g key={tick}>
            <line x1={LEFT} x2={WIDTH - RIGHT} y1={y(tick)} y2={y(tick)} className="ay-chart__grid" />
            <text x={LEFT - 8} y={y(tick) + 4} textAnchor="end" className="ay-chart__label">
              {axisLabel(tick)}
            </text>
          </g>
        ))}
        {references.map((reference) => (
          <line key={reference.field} x1={LEFT} x2={WIDTH - RIGHT} y1={y(reference.value!)} y2={y(reference.value!)} className={`ay-chart__reference ay-tone-stroke--${reference.tone ?? 'negative'}`} />
        ))}
        {rows.map((row, index) => (
          <g key={index}>
            {bars.map((entry, position) => {
              const value = coordinate(pick(row, entry.field)) ?? 0;
              const x = cx(index) - (width * bars.length) / 2 + position * width;
              return <rect key={entry.field} x={x + 1} y={Math.min(y(value), y(0))} width={width - 2} height={Math.abs(y(0) - y(value))} rx={2} className={`ay-tone-fill--${entry.tone ?? 'neutral'}`} />;
            })}
            <text x={cx(index)} y={height - 12} textAnchor="middle" className="ay-chart__label">
              {label(row)}
            </text>
          </g>
        ))}
        {lines.map((entry) => {
          const points = rows.map((row, index) => ({ index, value: coordinate(pick(row, entry.field)), flagged: entry.flag_field !== undefined && pick(row, entry.flag_field) === true })).filter((point) => point.value !== null);
          if (points.length === 0) return null;
          return (
            <g key={entry.field} className={`ay-tone-stroke--${entry.tone ?? 'neutral'}`}>
              <polyline points={points.map((point) => `${cx(point.index)},${y(point.value!)}`).join(' ')} className={`ay-chart__line${entry.dashed === true ? ' ay-chart__line--dashed' : ''}`} />
              {points.map((point) => (
                <circle key={point.index} cx={cx(point.index)} cy={y(point.value!)} r={point.flagged ? 6 : 3.5} className={point.flagged ? 'ay-chart__point ay-chart__point--flagged' : 'ay-chart__point'} />
              ))}
            </g>
          );
        })}
      </svg>
    </>
  );
}
